import { useState, useCallback, useEffect, useRef } from 'react';

export const useHierarchyState = ({
    initialExpandedIds = [],
    initialSelectedIds = [],
    onExpand,
    onCollapse,
    onSelect,
    multiSelect,
    editMode = false,
}) => {
    // Local state for expansion
    const [localExpandedIds, setLocalExpandedIds] = useState(initialExpandedIds);

    // Local state for selection
    const [localSelectedIds, setLocalSelectedIds] = useState(initialSelectedIds);

    // Track last selected index for range selection
    const [lastSelectedIndex, setLastSelectedIndex] = useState(null);

    // Track flattened nodes for selection operations
    const [flattenedNodes, setFlattenedNodes] = useState([]);

    // Track last selected node ID for shift-click range selection
    const [lastSelectedId, setLastSelectedId] = useState(null);

    // Editing state - track which node is being edited
    const [editingNodeId, setEditingNodeId] = useState(null);

    // Track pending new node (for adding child)
    const [pendingNewNode, setPendingNewNode] = useState(null);

    // Ref to track if we're updating from internal changes
    const isInternalUpdate = useRef(false);

    // Sync expanded state when prop changes (controlled component)
    useEffect(() => {
        if (initialExpandedIds && Array.isArray(initialExpandedIds) && !isInternalUpdate.current) {
            setLocalExpandedIds(initialExpandedIds);
        }
        isInternalUpdate.current = false;
    }, [initialExpandedIds]);

    // Sync selected state when prop changes (controlled component)
    useEffect(() => {
        if (initialSelectedIds && Array.isArray(initialSelectedIds) && !isInternalUpdate.current) {
            setLocalSelectedIds(initialSelectedIds);
        }
        isInternalUpdate.current = false;
    }, [initialSelectedIds]);

    // Function to update flattened nodes from parent
    const updateFlattenedNodes = useCallback((nodes) => {
        setFlattenedNodes(nodes || []);
    }, []);

    // Handle toggle expand/collapse
    const handleToggleExpand = useCallback((node) => {
        const nodeId = node.id;
        const isExpanded = localExpandedIds.includes(nodeId);

        isInternalUpdate.current = true;

        if (isExpanded) {
            // Collapse
            const newExpandedIds = localExpandedIds.filter(id => id !== nodeId);
            setLocalExpandedIds(newExpandedIds);

            if (onCollapse) {
                onCollapse(node, newExpandedIds);
            }
        } else {
            // Expand
            const newExpandedIds = [...localExpandedIds, nodeId];
            setLocalExpandedIds(newExpandedIds);

            if (onExpand) {
                onExpand(node, newExpandedIds);
            }
        }
    }, [localExpandedIds, onExpand, onCollapse]);

    // Expand a specific node (without toggle)
    const handleExpand = useCallback((node) => {
        const nodeId = node.id;
        if (!localExpandedIds.includes(nodeId)) {
            isInternalUpdate.current = true;
            const newExpandedIds = [...localExpandedIds, nodeId];
            setLocalExpandedIds(newExpandedIds);

            if (onExpand) {
                onExpand(node, newExpandedIds);
            }
        }
    }, [localExpandedIds, onExpand]);

    // Expand multiple nodes at once
    const handleExpandMultiple = useCallback((nodeIds) => {
        const newIds = nodeIds.filter(id => !localExpandedIds.includes(id));
        if (newIds.length > 0) {
            isInternalUpdate.current = true;
            const newExpandedIds = [...localExpandedIds, ...newIds];
            setLocalExpandedIds(newExpandedIds);
        }
    }, [localExpandedIds]);

    // Handle selection
    const handleSelect = useCallback((node, toggleMode = false) => {
        const nodeId = node.id;
        let newSelectedIds;

        if (multiSelect && toggleMode) {
            // Toggle selection in multi-select mode
            if (localSelectedIds.includes(nodeId)) {
                newSelectedIds = localSelectedIds.filter(id => id !== nodeId);
            } else {
                newSelectedIds = [...localSelectedIds, nodeId];
            }
        } else {
            // Single selection (replace)
            newSelectedIds = [nodeId];
        }

        setLocalSelectedIds(newSelectedIds);
        setLastSelectedId(nodeId);

        // Update last selected index
        const index = flattenedNodes.findIndex(n => n.id === nodeId);
        setLastSelectedIndex(index);

        // Call callback
        if (onSelect) {
            onSelect(newSelectedIds);
        }
    }, [localSelectedIds, flattenedNodes, multiSelect, onSelect]);

    // Handle range selection (Shift + Click)
    const handleSelectRange = useCallback((endIndex) => {
        if (!multiSelect || lastSelectedIndex === null) {
            return;
        }

        const startIndex = Math.min(lastSelectedIndex, endIndex);
        const endIdx = Math.max(lastSelectedIndex, endIndex);

        const rangeNodeIds = flattenedNodes
            .slice(startIndex, endIdx + 1)
            .map(node => node.id);

        // Merge with existing selection
        const newSelectedIds = Array.from(new Set([...localSelectedIds, ...rangeNodeIds]));
        setLocalSelectedIds(newSelectedIds);

        // Call callback
        if (onSelect) {
            onSelect(newSelectedIds);
        }
    }, [multiSelect, lastSelectedIndex, flattenedNodes, localSelectedIds, onSelect]);

    // Handle range selection by node ID (for click handlers)
    const handleSelectRangeById = useCallback((targetNodeId) => {
        if (!multiSelect || !lastSelectedId) {
            return;
        }

        const startIndex = flattenedNodes.findIndex(n => n.id === lastSelectedId);
        const endIndex = flattenedNodes.findIndex(n => n.id === targetNodeId);

        if (startIndex === -1 || endIndex === -1) {
            return;
        }

        const minIndex = Math.min(startIndex, endIndex);
        const maxIndex = Math.max(startIndex, endIndex);

        const rangeNodeIds = flattenedNodes
            .slice(minIndex, maxIndex + 1)
            .map(node => node.id);

        // Replace selection with range
        setLocalSelectedIds(rangeNodeIds);
        setLastSelectedIndex(endIndex);

        // Call callback
        if (onSelect) {
            onSelect(rangeNodeIds);
        }
    }, [multiSelect, lastSelectedId, flattenedNodes, onSelect]);

    // Handle select all
    const handleSelectAll = useCallback(() => {
        if (!multiSelect) {
            return;
        }

        const allNodeIds = flattenedNodes.map(node => node.id);
        setLocalSelectedIds(allNodeIds);

        // Call callback
        if (onSelect) {
            onSelect(allNodeIds);
        }
    }, [multiSelect, flattenedNodes, onSelect]);

    // Handle clear selection
    const handleClearSelection = useCallback(() => {
        setLocalSelectedIds([]);
        setLastSelectedIndex(null);
        setLastSelectedId(null);

        // Call callback
        if (onSelect) {
            onSelect([]);
        }
    }, [onSelect]);

    // Start editing a node (inline rename)
    const startEditing = useCallback((nodeId) => {
        if (editMode) {
            setEditingNodeId(nodeId);
        }
    }, [editMode]);

    // Stop editing
    const stopEditing = useCallback(() => {
        setEditingNodeId(null);
        setPendingNewNode(null);
    }, []);

    // Set a pending new node (for add child operation)
    const startAddingChild = useCallback((parentId, nodeType = 'location') => {
        if (editMode) {
            const tempId = `new-${Date.now()}`;
            setPendingNewNode({
                id: tempId,
                parentId,
                type: nodeType,
                name: '',
                isNew: true,
            });
            setEditingNodeId(tempId);
        }
    }, [editMode]);

    // Confirm pending new node creation
    const confirmNewNode = useCallback((name) => {
        if (pendingNewNode) {
            const newNode = {
                ...pendingNewNode,
                name,
                isNew: false,
            };
            setPendingNewNode(null);
            setEditingNodeId(null);
            return newNode;
        }
        return null;
    }, [pendingNewNode]);

    // Cancel pending new node
    const cancelNewNode = useCallback(() => {
        setPendingNewNode(null);
        setEditingNodeId(null);
    }, []);

    return {
        localExpandedIds,
        localSelectedIds,
        lastSelectedIndex,
        lastSelectedId,
        editingNodeId,
        pendingNewNode,
        handleToggleExpand,
        handleExpand,
        handleExpandMultiple,
        handleSelect,
        handleSelectRange,
        handleSelectRangeById,
        handleSelectAll,
        handleClearSelection,
        updateFlattenedNodes,
        startEditing,
        stopEditing,
        startAddingChild,
        confirmNewNode,
        cancelNewNode,
    };
};
